import { ComputeFunction, ComputeFunctionType } from './computeFunctionTypes'

// Types for NII Screening functions
export type Tri = 'low' | 'med' | 'high'

// Helper function to convert tri-state values to numeric scores
const TRI_SCORES: Record<Tri, number> = {
  low: 0,
  med: 1,
  high: 2,
}

const scoreTri = (v: Tri): number => {
  return TRI_SCORES[v]
}

// NII Screening outcome computation function
export function computeScreeningOutcome(
  confidence: Tri,
  previousInspectionEffectiveness: Tri,
  severityAndRateOfDegradation: Tri
): boolean {
  const c = scoreTri(confidence)
  const p = scoreTri(previousInspectionEffectiveness)
  const s = scoreTri(severityAndRateOfDegradation)

  // Confidence Low (0)
  if (c === 0) {
    if (p === 0) return false // All severity levels fail
    if (p === 1) return s === 0 // Only severity low passes
    if (p === 2) return s !== 2 // Only high severity fails
  }

  // Confidence Med (1)
  if (c === 1) {
    if (p === 0) return s === 0 // Only low severity passes
    if (p === 1) return s <= 1 // Low and med pass, high fails
    if (p === 2) return true // All severity levels pass
  }

  // Confidence High (2)
  if (c === 2) {
    if (p === 0) return s !== 2 // Only high severity fails
    if (p === 1) return true // All severity levels pass
    if (p === 2) return true // All severity levels pass
  }

  return false
}

export const computeFunctionRegistry: Record<
  ComputeFunctionType,
  ComputeFunction
> = {
  [ComputeFunctionType.BOOLEAN_WEIGHTED_SCORE]: (params, context) => {
    const functionSpec = context?.functionSpec as
      | {
          threshold: number
          parameters: Array<{
            field: string
            weight: number
            inverted?: boolean
          }>
        }
      | undefined

    if (!functionSpec?.threshold || !functionSpec.parameters) return false

    const totalScore = functionSpec.parameters
      .map((paramSpec) => {
        const value = params[paramSpec.field] as boolean | undefined
        if (value === undefined) return 0

        const shouldScore = paramSpec.inverted ? !value : value
        return shouldScore ? paramSpec.weight : 0
      })
      .reduce((sum, weight) => sum + weight, 0)

    return totalScore <= functionSpec.threshold
  },

  [ComputeFunctionType.SCREENING_OUTCOME]: (params, context) => {
    const functionSpec = context?.functionSpec as
      | {
          parameters: Array<{
            field: string
            type: string
            defaultValue?: string
          }>
        }
      | undefined

    if (!functionSpec?.parameters || functionSpec.parameters.length !== 3)
      return false

    // Get the three tri-state parameters dynamically from the config
    const triStateParams = functionSpec.parameters.filter(
      (p) => p.type === 'tri-state'
    )
    if (triStateParams.length !== 3) return false

    // Extract values for each parameter, using field names from config
    const values = triStateParams.map((param) => {
      return (params[param.field] ?? param.defaultValue ?? 'low') as Tri
    })

    // Call the function with the three tri-state values
    return computeScreeningOutcome(values[0], values[1], values[2])
  },
}

export function getComputeFunction(
  functionType: ComputeFunctionType
): ComputeFunction | undefined {
  return computeFunctionRegistry[functionType]
}

export function isWeightedScoringFunction(
  functionType: ComputeFunctionType
): boolean {
  return functionType === ComputeFunctionType.BOOLEAN_WEIGHTED_SCORE
}

export function isTriStateFunction(functionType: ComputeFunctionType): boolean {
  return functionType === ComputeFunctionType.SCREENING_OUTCOME
}

export function getAllFunctionTypes(): ComputeFunctionType[] {
  return Object.values(ComputeFunctionType)
}
